/* eslint-disable no-unused-vars */
import {inject, observer} from 'mobx-react';
import qs from 'qs';
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {Container} from 'reactstrap';
import {from, of, zip} from 'rxjs';
import {groupBy, mergeAll, mergeMap, reduce, tap, toArray} from 'rxjs/operators';
import {MapContext} from '../../apiContexts';
import useTimezone from '../../hooks/use-timezone';
import useReportError from '../../hooks/use-report-error';
import {buildRouteObservation, reportDeviceColor} from '../../helpers/report';
import {getRouteReport} from '../../services/reportService';
import RouteList from '../../views/reports/route-list';

const RouteReportContainer = ({
  match,
  location,
  listOnly = false,
  mapRef,
  ...props
}) => {
  const [report, setReport] = useState([]);
  const [positions, setPositions] = useState();
  const [selectedPosition, setSelectedPosition] = useState();
  const [markers, setMarkers] = useState();
  const [deviceColors, setDeviceColors] = useState(new Map());
  const [loading, setLoading] = useState(true);

  const {map, devices, server} = props;
  const intl = useIntl();
  const {timezone} = useTimezone();
  const notifyReportError = useReportError();

  const search = useMemo(() => location.search, [location.search]);

  const {setData, setSelectedItem, setSelectedRow, replay} = useContext(MapContext);

  const activeRef = useRef();
  const [replayScrollIndex, setReplayScrollIndex] = useState(null);

  const getReport = useCallback(async (params, markers) => {
    setLoading(true);
    from(getRouteReport(params))
        .pipe(
            tap((report) => {
              const cache = new Map();
              setReport(
                  report.map((e) => {
                    const {deviceId} = e;
                    if (!cache.has(deviceId)) {
                      cache.set(deviceId, devices.findById(deviceId).name);
                    }
                    e.name = cache.get(deviceId);
                    e.observation = buildRouteObservation(e, intl);
                    return e;
                  }),
              );
            }),
            mergeAll(),
            groupBy(
                (position) => position.deviceId,
                (p) => p,
            ),
            mergeMap((group) => zip(of(group.key), group.pipe(toArray()))),
            reduce(
                (acc, [deviceId, positions]) => (
                  acc.set(deviceId, {
                    device: devices.findById(+deviceId),
                    positions,
                  }),
                  acc
                ),
                new Map(),
            ),
        )
        .subscribe(
            (data) => {
              data = [...data.values()];
              setPositions(data);
              // Uma tabela so, do dispositivo para a cor, entregue tanto para a lista quanto para o
              // mapa. Cada lado derivar a propria cor da ordem em que o dispositivo aparece daria o
              // mesmo resultado quase sempre -- e discordaria no dia em que uma das ordens mudasse.
              const cores = new Map(
                  data.map(({device}, indice) => [Number(device.id), reportDeviceColor(indice)]),
              );
              setDeviceColors(cores);
              setData({type: 'route', data, markers: markers === 'true', deviceColors: cores});
              setLoading(false);
            },
            (error) => {
              setLoading(false);
              notifyReportError(error);
            },
        );
  }, []);

  const onSelect = useCallback(
      ({index, data: position}) => {
        if (activeRef && activeRef.current === index) {
          // Mesma linha de novo: o mapa pode ter sido movido depois do primeiro clique. Uma referência nova
          // faz o mapa voltar ao ponto da linha.
          setSelectedItem({...report[index]});
          return;
        }

        const item = report[index];
        let itemActive = null;

        if (activeRef && activeRef.current >= 0) {
          itemActive = report[activeRef.current];
        }

        const items = report.concat();

        items[index] = {...item, isActive: true};
        itemActive &&
        (items[activeRef.current] = {...itemActive, isActive: false});

        activeRef.current = index;

        setReport(items);
        setSelectedItem(item);

        // A linha escolhida e marcada nos dois caminhos: clique no marcador do mapa (vem `position`)
        // e clique na linha da tabela (so `item`).
        const escolhida = position || item;
        if (escolhida) {
          setSelectedRow({
            id: escolhida.id,
            deviceId: escolhida.deviceId ?? item?.deviceId,
            latitude: escolhida.latitude ?? item?.latitude,
            longitude: escolhida.longitude ?? item?.longitude,
          });
        }
      },
      [report],
  );

  useEffect(() => {
    // follows replay progress without recentering the map on every tick (unlike onSelect)
    if (!replay?.replayPositionId) return;

    const index = report.findIndex((r) => r.id === replay.replayPositionId);
    if (index === -1 || index === activeRef.current) return;

    const item = report[index];
    let itemActive = null;
    if (activeRef.current >= 0) itemActive = report[activeRef.current];

    const items = report.concat();
    items[index] = {...item, isActive: true};
    if (itemActive) items[activeRef.current] = {...itemActive, isActive: false};

    activeRef.current = index;
    setReport(items);
    setReplayScrollIndex(index);
  }, [replay?.replayPositionId, report]);

  useEffect(() => {
    if (search && devices.list.length && location.hash === '#route') {
      const {markers, ...params} = qs.parse(search, {
        ignoreQueryPrefix: true,
        comma: true,
      });

      setMarkers(markers);

      getReport(qs.stringify(params, {indices: false}), markers);
    }
  }, [search, devices.list]);

  return (
    <Container fluid className="px-4 position-relative h-100">
      <RouteList
        data={report}
        deviceColors={deviceColors}
        loading={loading}
        speedUnit={server.attributes && server.attributes.speedUnit}
        scrollToIndex={replayScrollIndex}
        {...{timezone, onSelect}}
      />
    </Container>
  );
};

const mapping = ({store}) => ({
  auth: store.auth,
  session: store.session,
  map: store.map,
  server: store.server,
  devices: store.devices,
});

export default inject(mapping)(observer(RouteReportContainer));
